import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'sonner';
import * as api from '../utils/api';
import { v4 as uuidv4 } from 'uuid';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Initialize session ID
  useEffect(() => {
    let sid = localStorage.getItem('cart_session_id');
    if (!sid) {
      sid = uuidv4();
      localStorage.setItem('cart_session_id', sid);
    }
    setSessionId(sid);
    loadCart(sid);
  }, []);

  const loadCart = async (sid) => {
    try {
      setInitialLoading(true);
      const cart = await api.getCart(sid);
      if (cart && cart.items) {
        setCartItems(cart.items);
      }
    } catch (error) {
      console.error('Error loading cart:', error);
    } finally {
      setInitialLoading(false);
    }
  };

  const addToCart = async (service) => {
    setLoading(true);
    try {
      // Pass business info if it exists on the service object
      const businessInfo = service.businessInfo || null;
      const result = await api.addToCart(sessionId, service.id, 1, businessInfo);
      if (result.success && result.cart) {
        setCartItems(result.cart.items || []);
        toast.success('Service added to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Failed to add service to cart');
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (serviceId) => {
    setLoading(true);
    try {
      const result = await api.removeFromCart(sessionId, serviceId);
      if (result.success && result.cart) {
        setCartItems(result.cart.items || []);
        toast.success('Service removed from cart');
      }
    } catch (error) {
      console.error('Error removing from cart:', error);
      toast.error('Failed to remove service');
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (serviceId, quantity) => {
    if (quantity < 1) {
      removeFromCart(serviceId);
      return;
    }
    
    setLoading(true);
    try {
      const result = await api.updateCartQuantity(sessionId, serviceId, quantity);
      if (result.success && result.cart) {
        setCartItems(result.cart.items || []);
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
      toast.error('Failed to update quantity');
    } finally {
      setLoading(false);
    }
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getTotal = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getItemCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  const value = {
    cartItems,
    isCartOpen,
    setIsCartOpen,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getTotal,
    getItemCount,
    sessionId,
    loading,
    initialLoading,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
